/**
 * generatePreProcessJsonMetadata_Template.ts - Template retrieval for metadata generation
 * Port of C# GeneratePreProcessJsonMetadata_Template.cs
 * Pure ES module - works in both Node.js and browser via bundler
 */

import type { TemplateInfo } from '../../model/appSiteMetadata.js';

/**
 * Result type for getTemplate
 */
export interface GetTemplateMetadataResult {
    template: TemplateInfo | null;
    foundKey: string;
}

/**
 * Retrieves a template from the templates dictionary using nested structure
 * Handles component reuse pattern: CurrentTemplateName_Number_PlaceholderName
 */
export function getTemplate(
    allTemplates: Map<string, Map<string, TemplateInfo>>,
    appSiteName: string,
    templateName: string,
    currentTemplateName: string,
    appView: string,
    appViewPrefix: string
): GetTemplateMetadataResult {
    // Check for component reuse pattern: CurrentTemplateName_Number_PlaceholderName
    let baseTemplateName = templateName;
    const parts = templateName.split('_');

    // Parse pattern: must have at least 3 parts (CurrentName_Number_PlaceholderName)
    if (parts.length >= 3 && currentTemplateName) {
        // Look for a number in the middle parts
        for (let i = 1; i < parts.length - 1; i++) {
            if (!isNaN(parseInt(parts[i], 10))) {
                // Check if prefix matches currentTemplateName
                const prefixParts: string[] = [];
                for (let j = 0; j < i; j++) {
                    prefixParts.push(parts[j]);
                }
                const prefix = prefixParts.join('_');

                if (prefix.toLowerCase() === currentTemplateName.toLowerCase()) {
                    // Extract base component name (everything after the number)
                    const remainingParts: string[] = [];
                    for (let j = i + 1; j < parts.length; j++) {
                        remainingParts.push(parts[j]);
                    }
                    baseTemplateName = remainingParts.join('_');
                    break;
                }
            }
        }
    }

    let appViewTemplateName = baseTemplateName;
    if (appViewTemplateName.toLowerCase().startsWith(appViewPrefix.toLowerCase()) && appView) {
        appViewTemplateName = appView + appViewTemplateName.substring(appViewPrefix.length);
    }

    // Find the component (case-insensitive) - O(1) lookup with nested structure
    // NOTE: allTemplates is a Map<appSiteName, Map<templateName, templateInfo>>
    let foundComponent: TemplateInfo | null = null;
    let foundKey = '';

    // Resolution order:
    // 1. Try AppView-specific name in primary AppSite (e.g., NextHeader in HtmlIndexView)
    // 2. Try original name in primary AppSite (e.g., MainHeader in HtmlIndexView) - AppView fallback
    // 3. Try AppView-specific name in SearchAppSites (e.g., NextHeader in HtmlIndex)
    // 4. Try original name in SearchAppSites (e.g., MainHeader in HtmlIndex)

    if (allTemplates.has(appSiteName)) {
        const appSiteTemplates = allTemplates.get(appSiteName)!;

        // Step 1: Try AppView-specific name in primary AppSite
        for (const [key, value] of appSiteTemplates.entries()) {
            if (key.toLowerCase() === appViewTemplateName.toLowerCase()) {
                foundComponent = value;
                foundKey = appSiteName + '_' + key;
                return { template: foundComponent, foundKey };
            }
        }

        // Step 2: Try original name in primary AppSite (AppView fallback)
        // Only if appViewTemplateName is different from templateName
        if (appViewTemplateName.toLowerCase() !== templateName.toLowerCase()) {
            for (const [key, value] of appSiteTemplates.entries()) {
                if (key.toLowerCase() === templateName.toLowerCase()) {
                    foundComponent = value;
                    foundKey = appSiteName + '_' + key;
                    return { template: foundComponent, foundKey };
                }
            }
        }
    }

    // Step 3 & 4: Search in SearchAppSites
    // Try AppView-specific name first, then original name
    const searchNames = [appViewTemplateName];
    if (appViewTemplateName.toLowerCase() !== templateName.toLowerCase()) {
        searchNames.push(templateName);
    }

    for (const searchName of searchNames) {
        for (const [appSiteKey, appSiteTemplates] of allTemplates.entries()) {
            if (appSiteKey.toLowerCase() === appSiteName.toLowerCase()) {
                continue; // Skip primary AppSite (already checked)
            }

            for (const [templateKey, templateValue] of appSiteTemplates.entries()) {
                if (templateKey.toLowerCase() === searchName.toLowerCase()) {
                    foundComponent = templateValue;
                    foundKey = appSiteKey + '_' + templateKey;
                    return { template: foundComponent, foundKey };
                }
            }
        }
    }

    return { template: foundComponent, foundKey };
}
